import { Check, XClose } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useState } from 'react';
import { calculateOrderProgress } from 'src/app/Orders/helpers/calculateOrderProgress';
import { hasDeliveryPodDocument } from 'src/app/Orders/order.helpers';

export type OrderStage = 'booked' | 'dispatched' | 'in-transit' | 'delivered' | 'completed';

type StepStatus = 'completed' | 'in-progress' | 'pending' | 'error';

interface StatusStep {
    key: OrderStage;
    label: string;
    status: StepStatus;
    progress: number; // 0-100
}

const STATUS_STEPS_CONFIG: Array<{ key: OrderStage; label: string }> = [
    { key: 'booked' as OrderStage, label: 'Booked' },
    { key: 'in-transit' as OrderStage, label: 'In Transit' },
    { key: 'delivered' as OrderStage, label: 'Delivered' },
    { key: 'completed' as OrderStage, label: 'Completed' },
];

/**
 * Per-leg step columns (mirrors WAL's NBA step model):
 *   leg 1        → Booked | In Transit | Arrived
 *   middle legs  → In Transit | Arrived
 *   final leg    → In Transit | Delivered | Completed
 *   single leg   → Booked | In Transit | Delivered | Completed
 * "Booked" belongs to the order and only exists on the first leg. Later legs carry no
 * dispatch column at all — their journey starts when the shipment arrives at their stop.
 * "Completed" only exists where the customer's shipment completes. A non-final stop's
 * third column reads "Arrived", or "Delivered" when the customer's items drop there
 * (customer wording, 2026-09-05; the retired Upcoming Stop / Stop Reached jargon is gone).
 */
const buildStepsConfig = (isFirstLeg: boolean, isFinalLeg: boolean, dropsHere: boolean): Array<{ key: OrderStage; label: string }> => [
    ...(isFirstLeg ? [{ key: 'booked' as OrderStage, label: 'Booked' }] : []),
    { key: 'in-transit' as OrderStage, label: 'In Transit' },
    { key: 'delivered' as OrderStage, label: isFinalLeg || dropsHere ? 'Delivered' : 'Arrived' },
    ...(isFinalLeg ? [{ key: 'completed' as OrderStage, label: 'Completed' }] : []),
];

interface IProps {
    isProgressLoading: boolean;
    viewingLeg: any;
    orderDetails?: any;
    hasBookingError?: boolean;
    /** True when the viewed leg opens the route — only the first leg carries the "Booked" column */
    isFirstLeg?: boolean;
    /** True when the viewed leg is the route's final leg — non-final legs relabel "Delivered" as a stop and drop "Completed" */
    isFinalLeg?: boolean;
    /** False while the freight has NOT yet reached this leg's start — the whole bar stays pending with zero progress */
    isLegReached?: boolean;
    /** True when the customer's items are delivered at this non-final stop — its third column reads "Delivered" instead of "Arrived" */
    dropsHere?: boolean;
}

const ProgressList: React.FC<IProps> = ({ isProgressLoading, viewingLeg, orderDetails, hasBookingError = false, isFirstLeg = true, isFinalLeg = true, isLegReached = true, dropsHere = false }) => {
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setRefreshKey((prev) => prev + 1);
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Calculate status progress for viewing leg with real-time progress calculation
    const statusSteps = useMemo(() => {
        const stepsConfig = buildStepsConfig(isFirstLeg, isFinalLeg, dropsHere);

        // No leg, or the freight hasn't reached this leg's start yet — nothing may run:
        // every column pending, zero progress, no spinner, until the previous leg delivers.
        if (!viewingLeg || !isLegReached) {
            return stepsConfig.map((step) => ({
                key: step.key,
                label: step.label,
                status: 'pending' as StepStatus,
                progress: 0,
            }));
        }

        // In-transit progress % must be LEG-scoped: the viewed leg's own latest location
        // update. The order-level list mixes every leg's carrier pings, so falling back to
        // it on a multi-leg order would show another leg's transit progress on this bar —
        // only use the order-level value for single-leg orders where they are the same thing.
        const isSingleLegOrder = (orderDetails?.orderLegs?.length ?? 0) <= 1;
        const locationProgressPercentage =
            viewingLeg?.lastUpdatedLocation?.progressPercentage ??
            (isSingleLegOrder ? orderDetails?.orderLocationUpdate?.progressPercentage ?? orderDetails?.orderLocationUpdateList?.[0]?.progressPercentage ?? null : null);

        // Get progress data from backend logic
        // appearance.timezone hardcoded — CP has no dynamic timezone context
        const progress = calculateOrderProgress(viewingLeg, { timezone: 'America/New_York' }, locationProgressPercentage);

        // Extract actual status key to compare easily
        const currentStatusKey = viewingLeg.orderStatus?.toLowerCase().replace(/_/g, '-') ?? '';

        // POD uploaded after delivery → the order is COMPLETED for the customer: every
        // column (including Completed) shows done, instead of dripping internal invoicing
        // progress through the Completed bar. Only the POD of the leg that makes THIS
        // delivery counts — a merged span also carries the PODs of the inner legs it hides.
        const POD_COMPLETED_STATUSES = ['delivered', 'claim', 'dispute', 'invoice-created', 'invoice-sent', 'invoice-paid'];

        if (POD_COMPLETED_STATUSES.includes(currentStatusKey) && hasDeliveryPodDocument(viewingLeg)) {
            return stepsConfig.map((step) => ({
                key: step.key,
                label: step.label,
                status: 'completed' as StepStatus,
                progress: 100,
            }));
        }

        // Create simple maps for easy access
        const progressMap: any = {
            booked: progress.Booked,
            dispatched: progress.Dispatched,
            'in-transit': progress.InTransit,
            delivered: progress.Delivery,
            completed: progress.Completed,
        };

        const inProgressMap: any = {
            booked: progress.BookedInProgress,
            dispatched: progress.DispatchedInProgress,
            'in-transit': progress.InTransitInProgress,
            delivered: progress.DeliveryInProgress,
            completed: progress.CompletedInProgress,
        };

        // Determine final status (completed / in-progress / pending / pending-dispatch / error)
        const determineStatus = (stepKey: string): StepStatus => {
            const pct = progressMap[stepKey] ?? 0;
            const isRunning = inProgressMap[stepKey] ?? false;

            // Define status hierarchy to check if we've moved past a step
            const statusOrder = ['booked', 'dispatched', 'in-transit', 'delivered', 'completed'];
            const currentStatusIndex = statusOrder.indexOf(currentStatusKey);
            const stepIndex = statusOrder.indexOf(stepKey);

            // Booked is always completed once the order exists in the system (booked on WAL platform)
            if (stepKey === 'booked' && currentStatusIndex >= 0) {
                return 'completed';
            }

            // If we've moved past this step (current status is later in sequence), mark as completed
            // Only check if both indices are valid (>= 0)
            if (currentStatusIndex >= 0 && stepIndex >= 0 && currentStatusIndex > stepIndex) {
                return 'completed';
            }

            // Fully done and not active → show completed checkmark
            if (pct >= 100 && !isRunning) return 'completed';

            // If this step matches current orderStatus → in-progress (unless already 100%)
            const isCurrent = currentStatusKey === stepKey;
            if (isCurrent && pct < 100) return 'in-progress';

            // Has progress and still running → loader (only if we haven't moved past this step)
            if (pct > 0 && isRunning) {
                // Only show in-progress if we haven't moved past this step
                if (currentStatusIndex < 0 || stepIndex < 0 || currentStatusIndex <= stepIndex) {
                    return 'in-progress';
                }
            }

            return 'pending';
        };

        // Build final UI structure — the third column keeps its configured label ("Arrived" /
        // "Delivered") and reads "Arriving" / "Delivery" only while the shipment is closing in.
        return stepsConfig.map((step) => {
            const status = determineStatus(step.key);
            let label = step.label;

            if (step.key === 'delivered' && status !== 'completed' && (status === 'in-progress' || progress.isNearDelivery)) {
                label = step.label === 'Arrived' ? 'Arriving' : 'Delivery';
            }

            return {
                key: step.key,
                label,
                status,
                progress: progressMap[step.key] ?? 0,
            };
        });
    }, [
        viewingLeg,
        viewingLeg?.orderStatus,
        refreshKey,
        orderDetails?.orderLocationUpdate?.progressPercentage,
        orderDetails?.orderLocationUpdateList,
        orderDetails?.orderLegs?.length,
        hasBookingError,
        isFirstLeg,
        isFinalLeg,
        isLegReached,
    ]);

    const renderStageIcon = (status: StatusStep['status']) => {
        switch (status) {
            case 'completed':
                return (
                    <div className="rounded-xl w-3.5 h-3.5 flex items-center justify-center bg-fgSuccessPrimary">
                        <Check className="w-2.5 h-2.5 text-white" />
                    </div>
                );
            case 'error':
                return <XClose className="w-3.5 h-3.5 text-fgErrorPrimary" />;
            case 'in-progress':
                return <p className="loading-spinner-custom flex-none w-3.5 h-3.5"></p>;
            case 'pending':
                return <div className="rounded-xl w-3.5 h-3.5 flex items-center justify-center border border-utilityGray200"></div>;
            default:
                return null;
        }
    };

    if (isProgressLoading) {
        return (
            <>
                {/* Desktop loading */}
                <div className="p-3 hidden lg:flex items-center gap-x-[32px]">
                    {STATUS_STEPS_CONFIG.map((step) => (
                        <div key={step.key} className="flex-1 space-y-2.5">
                            <div className="flex items-center gap-x-1">
                                <div className="rounded-xl w-3.5 h-3.5 custom-loading" />
                                <h6 className="text-grayLight600 text-xs font-normal custom-loading">{step.label}</h6>
                            </div>
                            <progress className="progress max-w-full h-[5px] flex bg-utilityGray200 progress-success custom-loading" value={0} max="100"></progress>
                        </div>
                    ))}
                </div>
                {/* Mobile loading */}
                <div className="px-3 pt-3 flex flex-col lg:hidden">
                    {STATUS_STEPS_CONFIG.map((step, index) => {
                        const isLast = index === STATUS_STEPS_CONFIG.length - 1;

                        return (
                            <div key={step.key} className="flex gap-x-2">
                                <div className="flex flex-col items-center w-2">
                                    <span className="w-2 h-2 rounded-full flex-none mt-[3px] bg-utilityGray300 custom-loading" />
                                    {!isLast && <div className="w-0.5 flex-1 bg-utilityGray200" />}
                                </div>
                                <h6 className={`text-grayLight600 text-xs font-normal custom-loading ${isLast ? 'pb-0' : 'pb-3'}`}>{step.label}</h6>
                            </div>
                        );
                    })}
                </div>
            </>
        );
    }

    return (
        <>
            {/* Desktop — horizontal with progress bars */}
            <div className="p-3 hidden lg:flex items-center gap-x-[32px]">
                {statusSteps.map((step) => (
                    <div key={step.key} className="flex-1 space-y-2.5">
                        <div className="flex items-center gap-x-1">
                            {renderStageIcon(step.status)}
                            <h6 className="text-grayLight600 text-xs font-normal text-nowrap">{step.label}</h6>
                        </div>
                        <progress className="progress max-w-full h-[5px] flex bg-utilityGray200 progress-success" value={step.status === 'completed' ? 100 : step.progress} max="100"></progress>
                    </div>
                ))}
            </div>
            {/* Mobile — vertical timeline */}
            <div className="px-4 pt-3 pb-3 flex flex-col lg:hidden">
                {statusSteps.map((step, index) => {
                    const isLast = index === statusSteps.length - 1;
                    const lineColorClass = step.status === 'completed' ? 'bg-fgSuccessPrimary' : 'bg-utilityGray200';

                    return (
                        <div key={step.key} className="flex gap-x-2.5 text-center align-items: anchor-center;">
                            <div className="flex flex-col items-center w-2">
                                <span className="flex-none">{renderStageIcon(step.status)}</span>
                                {!isLast && <div className={`w-0.5 flex-1 ${lineColorClass}`} />}
                            </div>
                            <h6 className={`text-xs font-normal ${isLast ? 'pb-0' : 'pb-2.5'} text-grayLight600 leading-[16px]`}>{step.label}</h6>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default ProgressList;
